// SPA navigation indirection for non-React modules (apiClient interceptors).
//
// Why this exists
// ---------------
// ``apiClient`` runs outside the React tree, so it cannot call the router's
// ``useNavigate`` hook. Historically it used ``window.location.assign`` to
// redirect (e.g. the forced-change-password interception). That is a FULL
// PAGE RELOAD, which wipes the in-memory ``useAuthStore`` access token (the
// token lives in memory only — no persist middleware, by design HARD-10).
//
// For a temp-password-flagged principal that reload is fatal: ``hydrateOnMount``
// then POSTs ``/auth/refresh`` to restore the token, but RB-19a blocks refresh
// while ``requires_password_reset = TRUE`` (403 password_change_required). The
// store stays anonymous, the change-password POST goes out with NO Authorization
// header, and the backend returns 401 — the SA can never complete first login.
//
// The fix: do an in-app (SPA) navigation that preserves the in-memory token.
// ``main.tsx`` registers the router's ``navigate`` here at bootstrap; modules
// outside React call ``spaNavigate(path)``. If no navigate has been registered
// yet (e.g. a redirect fires before bootstrap completes), we fall back to a
// hard ``window.location.assign`` so the redirect still happens.

type NavigateFn = (path: string) => void;

let registeredNavigate: NavigateFn | null = null;

export function registerNavigate(fn: NavigateFn): void {
  registeredNavigate = fn;
}

export function spaNavigate(path: string): void {
  if (registeredNavigate !== null) {
    registeredNavigate(path);
    return;
  }
  // Fallback — no SPA navigator available yet. Hard navigation still works,
  // it just loses the in-memory token; acceptable only as a last resort.
  if (typeof window !== "undefined") {
    window.location.assign(path);
  }
}
